/* ============================================================
   HAPPY CLUCK - Chicken Dewormer PDP interactions
   Sticky sub-nav scroll-spy + smooth scroll, the interactive
   ingredient explorer, and the buy box (flock math, plan toggle,
   Daily Duo, per-day pricing, gallery, sticky, sheet, modal).
   Vanilla, dep-free.

   PRICING MATH (verified against the LIVE Shopify cart API, 2026-07-01):
   One-time volume discounts are Shopify automatic discounts
   (2+ bags = 5%, 3+ = 10%, 5+ = 15%). Shopify computes the discount
   PER UNIT and FLOORS it to the cent, then multiplies by quantity:
     perUnitDisc = floor(2999 * pct / 100)
     lineTotal   = (2999 - perUnitDisc) * qty
   Live-verified: 2 bags $57.00 (disc 298), 3 bags $81.00 (disc 897),
   4 bags $108.00 (disc 1196), 5 bags $127.50 (disc 2245).
   Display MUST use this exact math so displayed price = charged price.
   ============================================================ */
(function () {
  "use strict";

  /* ---------- Shared config (single source of truth in hc-header.liquid) ---------- */
  function readCartCfg() {
    try { return JSON.parse(document.getElementById("hc-cart-config").textContent) || {}; }
    catch (e) { return {}; }
  }
  var CFG = readCartCfg();
  /* config stores dollars; convert to cents. Fallbacks match the live store settings. */
  var SHIP_THRESHOLD = Math.round((CFG.freeShipThreshold != null ? CFG.freeShipThreshold : 70) * 100);
  var SHIP_FEE = Math.round((CFG.shipFee != null ? CFG.shipFee : 8) * 100);

  /* ---------- Sticky sub-nav: smooth scroll + scroll-spy + drag-scroll ---------- */
  function initSubnav() {
    var nav = document.querySelector("[data-hc-subnav]");
    if (!nav) return;
    if (nav.dataset.hcSubnavBound === "1") return;
    nav.dataset.hcSubnavBound = "1";

    var scroller = nav.querySelector(".hc-subnav__links") || nav;
    var links = Array.prototype.slice.call(nav.querySelectorAll(".hc-subnav__link"));
    if (!links.length) return;

    var targets = links
      .map(function (l) {
        var id = (l.getAttribute("href") || "").replace(/^#/, "");
        var el = id ? document.getElementById(id) : null;
        return el ? { link: l, el: el } : null;
      })
      .filter(Boolean);

    /* offset = whatever sits above content as you scroll: the sticky sub-nav.
       The site header is non-sticky on this PDP, so we only clear the nav itself. */
    function navOffset() { return nav.offsetHeight + 8; }

    /* bring the active pill into view inside the horizontal scroller */
    function ensureVisible(link) {
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
      var lLeft = link.offsetLeft;
      var lRight = lLeft + link.offsetWidth;
      var vLeft = scroller.scrollLeft;
      var vRight = vLeft + scroller.clientWidth;
      var pad = 16;
      if (lLeft < vLeft + pad) {
        scroller.scrollTo({ left: lLeft - pad, behavior: "smooth" });
      } else if (lRight > vRight - pad) {
        scroller.scrollTo({ left: lRight - scroller.clientWidth + pad, behavior: "smooth" });
      }
    }

    function setActive(link) {
      links.forEach(function (l) { l.classList.remove("is-active"); });
      link.classList.add("is-active");
      ensureVisible(link);
    }

    /* smooth-scroll to the EXACT top of each section */
    links.forEach(function (l) {
      l.addEventListener("click", function (e) {
        var id = (l.getAttribute("href") || "").replace(/^#/, "");
        var el = id ? document.getElementById(id) : null;
        if (!el) return;
        e.preventDefault();
        var y = el.getBoundingClientRect().top + window.pageYOffset - navOffset();
        window.scrollTo({ top: y, behavior: "smooth" });
        history.replaceState(null, "", "#" + id);
        setActive(l);
        /* lazy media above the target can shift layout while the smooth
           scroll runs, leaving the section short of the nav. One delayed
           correction re-lands it exactly. */
        clearTimeout(nav._hcCorrT);
        nav._hcCorrT = setTimeout(function () {
          var top = el.getBoundingClientRect().top;
          var want = navOffset();
          if (Math.abs(top - want) > 40) {
            window.scrollTo({ top: top + window.pageYOffset - want, behavior: "smooth" });
          }
        }, 900);
      });
    });

    /* mouse drag-to-scroll on the horizontal strip (touch drag is native).
       Skip on touch/coarse-pointer devices: synthetic mouse events there toggle the
       is-dragging class (pointer-events:none on links) + a capture-phase click
       suppressor, which together swallow tap-to-scroll on mobile. Touch scrolls natively. */
    (function enableDragScroll() {
      if (!window.matchMedia || !window.matchMedia("(pointer: fine)").matches) return;
      var down = false, startX = 0, startLeft = 0, moved = false;
      scroller.addEventListener("mousedown", function (e) {
        down = true; moved = false;
        startX = e.pageX; startLeft = scroller.scrollLeft;
        /* IMPORTANT: do NOT add .is-dragging here. The class disables
           pointer-events on the links, so adding it on plain mousedown
           swallowed every desktop click. Only a real drag (>4px) counts. */
      });
      window.addEventListener("mousemove", function (e) {
        if (!down) return;
        var dx = e.pageX - startX;
        if (!moved && Math.abs(dx) > 4) {
          moved = true;
          scroller.classList.add("is-dragging");
        }
        if (moved) scroller.scrollLeft = startLeft - dx;
      });
      window.addEventListener("mouseup", function () {
        down = false;
        scroller.classList.remove("is-dragging");
      });
      /* prevent a drag from also firing the link click */
      scroller.addEventListener("click", function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    })();

    /* scroll-spy: deterministic, position-based (the old IO band-entry spy
       could hold a stale pill when a position was reached without a section
       edge crossing the observation band, e.g. after fast flicks). */
    var spyTicking = false;
    function spyUpdate() {
      spyTicking = false;
      /* active = section whose top sits closest above the reading line
         (just below the sticky nav, ~38% into the viewport) */
      var line = navOffset() + Math.round((window.innerHeight || 800) * 0.38);
      /* pick max top <= line - robust even when the DOM order differs
         from the visual order */
      var current = null, best = -Infinity;
      targets.forEach(function (t) {
        var top = t.el.getBoundingClientRect().top;
        if (top <= line && top > best) { best = top; current = t; }
      });
      if (current && !current.link.classList.contains("is-active")) setActive(current.link);
    }
    window.addEventListener("scroll", function () {
      if (!spyTicking) { spyTicking = true; window.requestAnimationFrame(spyUpdate); }
    }, { passive: true });
    spyUpdate();
  }

  /* ---------- Ingredient explorer ---------- */
  function initIngredients() {
    document.querySelectorAll("[data-hc-ingx]").forEach(function (root) {
      var btns = Array.prototype.slice.call(root.querySelectorAll(".hc-ingx__btn"));
      var imgs = Array.prototype.slice.call(root.querySelectorAll(".hc-ingx__panel img"));
      var caps = Array.prototype.slice.call(root.querySelectorAll(".hc-ingx__capitem"));
      if (!btns.length) return;

      function show(key) {
        btns.forEach(function (b) { b.classList.toggle("is-active", b.dataset.key === key); });
        imgs.forEach(function (im) { im.classList.toggle("is-on", im.dataset.key === key); });
        caps.forEach(function (c) { c.hidden = c.dataset.key !== key; });
      }
      btns.forEach(function (b) {
        b.addEventListener("click", function () { show(b.dataset.key); });
        b.addEventListener("mouseenter", function () { show(b.dataset.key); });
      });
      show(btns[0].dataset.key);
    });
  }

  /* ---------- Any "to reviews" / "to buy box" anchor links ---------- */
  function anchorScrollOffset() {
    var nav = document.querySelector("[data-hc-subnav]");
    return (nav ? nav.offsetHeight : 60) + 12;
  }
  function initAnchorLinks() {
    var links = document.querySelectorAll('[data-hc-toreviews], a[href="#reviews"], a[href="#buy"], [data-hc-tobuy]');
    links.forEach(function (link) {
      if (link.dataset.hcRvBound === "1") return;
      link.dataset.hcRvBound = "1";
      link.addEventListener("click", function (e) {
        var href = link.getAttribute("href") || "";
        var id = link.hasAttribute("data-hc-tobuy") ? "buy" : href.replace(/^#/, "");
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        var y = target.getBoundingClientRect().top + window.pageYOffset - anchorScrollOffset();
        window.scrollTo({ top: y, behavior: "smooth" });
        history.replaceState(null, "", "#" + id);
      });
    });
  }

  /* ---------- Buy box: flock math, plan toggle, duo, gallery, sticky, modal ---------- */
  function initBuyBox() {
    var bb = document.querySelector("[data-hc-bb]");
    if (!bb) return;
    /* idempotency: shopify:section:load re-runs boot() - don't double-bind */
    if (bb.dataset.hcBound === "1") return;
    bb.dataset.hcBound = "1";

    /* ----- data ----- */
    var data = {};
    try {
      var dataEl = document.querySelector("[data-hc-bb-data]");
      if (dataEl) data = JSON.parse(dataEl.textContent);
    } catch (e) { data = {}; }

    var variantId = parseInt(bb.getAttribute("data-variant-id"), 10) || data.variantId;
    var price = parseInt(bb.getAttribute("data-price"), 10) || data.price || 0;
    var subPrice = parseInt(bb.getAttribute("data-sub-price"), 10) || data.subPrice || price;
    var subPct = parseInt(bb.getAttribute("data-sub-pct"), 10) || data.subPct || 0;
    var moneyFormat = (data && data.moneyFormat) || "${{amount}}";

    /* Daily Duo + cross-sell data */
    var eggVariantId = data.eggVariantId || 49851009171696;
    var eggSubPrice = data.eggSubPrice || 2399;
    var eggPlans = { 1: data.eggPlan1mo || 7827259632, 2: data.eggPlan2mo || 7827128560, 3: data.eggPlan3mo || 7827194096 };
    var sprayVariantId = data.sprayVariantId || 49851009270000;

    function eggPlanFor(interval) { return eggPlans[interval] || eggPlans[1]; }

    /* ----- money() using shop money_format ----- */
    function money(cents) {
      var amount = (Math.round(cents) / 100).toFixed(2);
      if (moneyFormat && /\{\{\s*amount\s*\}\}/.test(moneyFormat)) {
        return moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, amount);
      }
      // handle a couple of common alt placeholders, then fall back
      if (moneyFormat && /\{\{\s*amount_no_decimals\s*\}\}/.test(moneyFormat)) {
        return moneyFormat.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)));
      }
      return "$" + amount;
    }

    /* default tier = 1-5 hens: 1 bag, every 3 months, 3-month plan, midpoint 3 hens.
       One-time mode tracks its own qty (volume upsell). Default qty = 2 (2-pack).
       duo = Better-Together Golden Yolk add-on (subscribe mode only). */
    var state = { bags: 1, interval: 3, mid: 3, range: "1-5", plan: "subscribe", sellingPlan: data.plan3mo, qty: 2, duo: false };

    /* live one-time automatic-discount tiers: qty1=0, qty2=5%, qty3-4=10%, qty5+=15% */
    function tierDisc(q) {
      if (q >= 5) return 15;
      if (q >= 3) return 10;
      if (q >= 2) return 5;
      return 0;
    }
    /* one-time total = Shopify's real math: per-unit discount FLOORED to the cent, times qty
       (live cart verified: 2 bags $57.00, 3 $81.00, 4 $108.00, 5 $127.50). */
    function oneTimeTotalFor(q) {
      var perUnitDisc = Math.floor(price * tierDisc(q) / 100);
      return (price - perUnitDisc) * q;
    }
    function oneTimeTotal() { return oneTimeTotalFor(state.qty); }
    /* one-time per-bag price at qty q (volume discount applied, floored like Shopify) */
    function oneTimePerBag(q) { return price - Math.floor(price * tierDisc(q) / 100); }

    /* ----- helpers: pluralize + interval phrasing ----- */
    function bagWord(n) { return n === 1 ? "bag" : "bags"; }
    function everyPhrase(months) {
      return months === 1 ? "every month" : "every " + months + " months";
    }

    /* ----- per-day + per-hen line (COMPUTED, never hardcoded) -----
       perDay = (bags * subPrice) / (intervalMonths * 30); perHen = perDay / tier midpoint. */
    function perDayText() {
      var perDayC = (state.bags * subPrice) / (state.interval * 30);
      var perHenC = perDayC / (state.mid || 3);
      var henCeil = Math.ceil(perHenC);
      var henWord = (henCeil > perHenC) ? "under " : "about ";
      return "about " + money(perDayC) + "/day for your flock - " + henWord + henCeil + "¢ a hen";
    }

    /* ----- subscribe coverage sentence (prominent readout) per flock tier ----- */
    function coverageSentence(bags, interval) {
      var span = interval === 1 ? "about 1 month" : "about " + interval + " months";
      if (bags === 1 && interval === 3) {
        return "One bag covers a flock of up to 5 hens for about 3 months - just a daily sprinkle on feed.";
      }
      if (bags === 1 && interval === 2) {
        return "One bag covers a flock of 6 to 8 hens for about 2 months - just a daily sprinkle on feed.";
      }
      if (bags === 1 && interval === 1) {
        return "One bag covers a flock of 9 to 15 hens for about 1 month - just a daily sprinkle on feed.";
      }
      if (bags === 2) {
        return "Two bags cover a flock of 16 to 30 hens for about 1 month - just a daily sprinkle on feed.";
      }
      if (bags === 3) {
        return "Three bags cover a flock of 31 to 45 hens for about 1 month - just a daily sprinkle on feed.";
      }
      if (bags === 4) {
        return "Four bags cover a flock of 46 or more hens for about 1 month - just a daily sprinkle on feed.";
      }
      return bags + " " + bagWord(bags) + " for " + span + " - just a daily sprinkle on feed.";
    }

    /* ----- one-time flock suggestion ("30 hens = about 2 bags/month") ----- */
    function flockSuggestion() {
      if (state.interval === 1) {
        return state.range + " hens = about " + state.bags + " " + bagWord(state.bags) + "/month.";
      }
      return state.range + " hens = 1 bag lasts about " + state.interval + " months.";
    }

    /* ----- one-time AOV nudge line, by quantity ----- */
    function oneTimeNudge(q) {
      if (q <= 1) return "Add 1 more bag to save 5%.";
      if (q === 2) return "Add 1 more bag to save 10%.";
      if (q === 3) return "Add 2 more bags to save 15%.";
      if (q === 4) return "Add 1 more bag to save 15%.";
      return "You have our best one-time price. Subscribe to save " + subPct + "% on every order.";
    }
    /* ----- one-time savings summary for the prominent readout ----- */
    function oneTimeReadout(q) {
      var disc = tierDisc(q);
      var full = price * q;
      var total = oneTimeTotalFor(q);
      var per = Math.round(total / q);
      if (disc > 0) {
        return flockSuggestion() + " " + q + " " + bagWord(q) + " for " + money(total) + " (" + money(per) +
          "/ea) - you save " + money(full - total) + " at checkout.";
      }
      return flockSuggestion() + " 1 bag, " + money(total) + ". Add a 2nd bag to start saving 5%.";
    }

    /* ----- shipping note for the one-time card (threshold from hc-cart-config) ----- */
    function shipNote(total) {
      if (total >= SHIP_THRESHOLD) return "ships FREE (over " + money(SHIP_THRESHOLD) + ")";
      return "+ shipping - free over " + money(SHIP_THRESHOLD);
    }

    /* ----- prominent readout (cream box w/ checkmark) ----- */
    var freadoutText = bb.querySelector("[data-hc-freadout-text]");
    function shortSummary() {
      return state.bags + " " + bagWord(state.bags) + " " + everyPhrase(state.interval);
    }
    function updateReadout() {
      if (!freadoutText) return;
      if (state.plan === "onetime") {
        freadoutText.textContent = oneTimeReadout(state.qty);
      } else {
        freadoutText.textContent = coverageSentence(state.bags, state.interval);
      }
    }

    /* ----- render prices ----- */
    var ctaText = bb.querySelector("[data-hc-ctatext]");
    var stickyPriceEl = document.querySelector("[data-hc-sticky-price]");
    var stickyCtaEl = document.querySelector("[data-hc-sticky-cta]");

    function setText(sel, val) {
      bb.querySelectorAll(sel).forEach(function (el) { el.textContent = val; });
    }

    var nudgeEl = bb.querySelector("[data-hc-nudge]");
    var stepInput = bb.querySelector("[data-hc-step-input]");
    var tierBtns = Array.prototype.slice.call(bb.querySelectorAll("[data-hc-tier]"));

    /* fill the totals + per-bag + you-save figures inside each tier card */
    function fillTierCards(scope) {
      Array.prototype.slice.call(scope.querySelectorAll("[data-hc-tier],[data-hc-sheet-tier]")).forEach(function (card) {
        var q = parseInt(card.getAttribute("data-qty"), 10) || 1;
        if (q < 2) return;
        var total = oneTimeTotalFor(q);
        var per = Math.round(total / q);
        var saved = price * q - total;
        var tt = card.querySelector("[data-hc-tier-total]");
        var ea = card.querySelector("[data-hc-tier-ea]");
        var sv = card.querySelector("[data-hc-tier-save]");
        if (tt) tt.textContent = money(total);
        if (ea) ea.textContent = money(per) + "/ea";
        if (sv) sv.textContent = "You save " + money(saved);
      });
    }

    /* highlight the card matching the current qty (1/2/3); 4+ deselects all */
    function syncTierCards() {
      tierBtns.forEach(function (card) {
        var q = parseInt(card.getAttribute("data-qty"), 10) || 1;
        var on = q === state.qty;
        card.classList.toggle("is-active", on);
        card.setAttribute("aria-checked", on ? "true" : "false");
      });
      if (stepInput && document.activeElement !== stepInput) stepInput.value = state.qty;
    }

    /* ----- Daily Duo + native (Shop Pay) form elements ----- */
    var duoWrap = bb.querySelector("[data-hc-duo-wrap]");
    var duoBtns = Array.prototype.slice.call(bb.querySelectorAll("[data-hc-duo]"));
    var duoNote = bb.querySelector("[data-hc-duo-note]");
    var duoPriceEl = bb.querySelector("[data-hc-duo-price]");
    var nativeWrap = bb.querySelector("[data-hc-native]");
    var nativeQty = bb.querySelector("[data-hc-native-qty]");

    function syncDuoButtons(scopeBtns, duoOn) {
      scopeBtns.forEach(function (b) {
        var isDuo = (b.getAttribute("data-hc-duo") || b.getAttribute("data-hc-sheet-duo")) === "duo";
        b.classList.toggle("is-active", isDuo === duoOn);
      });
    }

    function render() {
      var base = price * state.bags;
      var sub = subPrice * state.bags;
      var duoAdd = state.duo ? eggSubPrice : 0;
      var otTotal = oneTimeTotal();
      setText("[data-hc-sub-now]", money(sub));
      setText("[data-hc-sub-was]", money(base));
      setText("[data-hc-sub-summary]", shortSummary());
      setText("[data-hc-perday]", perDayText());
      setText("[data-hc-ot-now]", money(otTotal));
      /* per-bag gap vs subscribe at the CURRENT qty (volume discounts shrink it) */
      setText("[data-hc-ot-delta]", "+" + money(Math.max(0, oneTimePerBag(state.qty) - subPrice)));
      setText("[data-hc-ship-note]", shipNote(otTotal));
      setText("[data-hc-subpct]", subPct + "%");
      /* modal "keep $X in your pocket" = real order-level gap at the chosen qty.
         The modal sits OUTSIDE [data-hc-bb], so update it document-wide. */
      var modalSaveVal = money(Math.max(0, oneTimeTotal() - subPrice * state.qty));
      Array.prototype.slice.call(document.querySelectorAll("[data-hc-modal-save]")).forEach(function (el) { el.textContent = modalSaveVal; });

      fillTierCards(bb);
      syncTierCards();
      if (nudgeEl) nudgeEl.textContent = oneTimeNudge(state.qty);

      /* duo UI */
      if (duoWrap) duoWrap.hidden = state.plan !== "subscribe";
      if (duoNote) duoNote.hidden = !state.duo || state.plan !== "subscribe";
      if (duoPriceEl) duoPriceEl.textContent = money(sub + eggSubPrice) + "/delivery";
      syncDuoButtons(duoBtns, state.duo);

      /* native Shop Pay form: ONE-TIME mode only, quantity kept in sync */
      if (nativeWrap) nativeWrap.hidden = state.plan !== "onetime";
      if (nativeQty) nativeQty.value = state.qty;

      var active = state.plan === "subscribe" ? sub + duoAdd : otTotal;
      var ctaLabel = (state.plan === "subscribe" && state.duo) ? "Add Duo to Cart - " : "Add to Cart - ";
      if (ctaText) ctaText.textContent = ctaLabel + money(active);
      if (stickyPriceEl) stickyPriceEl.textContent = money(active);
      if (stickyCtaEl) stickyCtaEl.textContent = "Add to Cart";
    }

    /* ----- flock selector (each pill carries bags + interval + plan + midpoint) -----
       Visible in BOTH modes. In one-time mode it pre-sets the suggested bag qty. ----- */
    var fbtns = Array.prototype.slice.call(bb.querySelectorAll("[data-hc-flock]"));
    function applyTier(b) {
      state.bags = parseInt(b.getAttribute("data-bags"), 10) || 1;
      state.interval = parseInt(b.getAttribute("data-interval"), 10) || 1;
      state.mid = parseInt(b.getAttribute("data-mid"), 10) || 3;
      state.range = b.getAttribute("data-range") || "";
      state.sellingPlan = parseInt(b.getAttribute("data-plan"), 10) || data.plan1mo;
    }
    fbtns.forEach(function (b) {
      b.addEventListener("click", function () {
        fbtns.forEach(function (x) { x.classList.remove("is-active"); });
        b.classList.add("is-active");
        applyTier(b);
        /* one-time mode: pre-set the suggested bag qty from the flock answer */
        if (state.plan === "onetime") state.qty = state.bags;
        updateReadout();
        render();
      });
    });

    /* ----- one-time tier cards + stepper (volume upsell) ----- */
    function setQty(q) {
      q = parseInt(q, 10);
      if (isNaN(q) || q < 1) q = 1;
      state.qty = q;
      updateReadout();
      render();
    }
    tierBtns.forEach(function (card) {
      card.addEventListener("click", function () {
        setQty(card.getAttribute("data-qty"));
      });
    });
    var stepBtns = Array.prototype.slice.call(bb.querySelectorAll("[data-hc-step]"));
    stepBtns.forEach(function (b) {
      b.addEventListener("click", function () {
        var delta = parseInt(b.getAttribute("data-hc-step"), 10) || 0;
        setQty(state.qty + delta);
      });
    });
    if (stepInput) {
      stepInput.addEventListener("input", function () {
        var v = parseInt(stepInput.value, 10);
        if (isNaN(v) || v < 1) return; /* let them clear/retype; commit on blur */
        state.qty = v;
        updateReadout();
        render();
      });
      stepInput.addEventListener("blur", function () { setQty(stepInput.value); });
    }

    /* ----- Daily Duo toggle (subscribe mode only) ----- */
    duoBtns.forEach(function (b) {
      b.addEventListener("click", function () {
        state.duo = b.getAttribute("data-hc-duo") === "duo";
        render();
      });
    });

    /* ----- plan toggle: flock selector stays visible in BOTH modes ----- */
    var planLabels = Array.prototype.slice.call(bb.querySelectorAll("[data-hc-planlabel]"));
    var qtyWrap = bb.querySelector("[data-hc-qty-wrap]");
    function syncSelectorVisibility() {
      var onetime = state.plan === "onetime";
      if (qtyWrap) qtyWrap.hidden = !onetime;
    }
    bb.querySelectorAll("[data-hc-plan]").forEach(function (input) {
      input.addEventListener("change", function () {
        if (!input.checked) return;
        state.plan = input.value;
        if (state.plan === "onetime") {
          /* carry the flock answer into a suggested qty */
          state.qty = state.bags > 1 ? state.bags : Math.max(state.qty, 1);
        }
        planLabels.forEach(function (l) {
          l.classList.toggle("is-on", l.getAttribute("data-hc-planlabel") === state.plan);
        });
        syncSelectorVisibility();
        updateReadout();
        render();
      });
    });

    /* ----- add to cart ----- */
    function addSubscribe(btn) {
      var items = [];
      var item = { id: variantId, quantity: state.bags };
      if (state.sellingPlan) item.selling_plan = state.sellingPlan;
      items.push(item);
      if (state.duo) {
        items.push({ id: eggVariantId, quantity: 1, selling_plan: eggPlanFor(state.interval) });
      }
      doCartAdd({ items: items }, btn);
    }
    function addOneTime(btn) {
      /* NO selling_plan: the Shopify automatic discount applies the same % at checkout */
      doCartAdd({ items: [{ id: variantId, quantity: state.qty }] }, btn);
    }
    function doCartAdd(payload, btn) {
      if (window.HCcart && typeof window.HCcart.add === "function") {
        window.HCcart.add(payload, btn);
      } else {
        if (btn) btn.disabled = true;
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(payload)
        }).then(function () {
          if (btn) btn.disabled = false;
          window.location.href = "/cart";
        }).catch(function () { if (btn) btn.disabled = false; });
      }
    }

    /* ----- cross-sell: Complete the routine ----- */
    Array.prototype.slice.call(document.querySelectorAll("[data-hc-xsell]")).forEach(function (btn) {
      btn.addEventListener("click", function () {
        var which = btn.getAttribute("data-hc-xsell");
        if (which === "egg") {
          /* subscription add-on on the same cadence as the flock answer */
          doCartAdd({ items: [{ id: eggVariantId, quantity: 1, selling_plan: eggPlanFor(state.interval) }] }, btn);
        } else if (which === "spray") {
          /* one-time only - Wound Spray has NO selling plans, ever */
          doCartAdd({ items: [{ id: sprayVariantId, quantity: 1 }] }, btn);
        }
      });
    });

    /* ----- subscribe-on-ATC modal ----- */
    var modal = bb.closest(".hc-bb") ? bb.closest(".hc-bb").querySelector("[data-hc-submodal]") : document.querySelector("[data-hc-submodal]");
    if (!modal) modal = document.querySelector("[data-hc-submodal]");
    function openModal() {
      if (!modal) return;
      modal.setAttribute("aria-hidden", "false");
      modal.classList.add("is-open");
    }
    function closeModal() {
      if (!modal) return;
      modal.setAttribute("aria-hidden", "true");
      modal.classList.remove("is-open");
    }

    var addBtns = Array.prototype.slice.call(document.querySelectorAll("[data-hc-add]"))
      .filter(function (b) { return b.closest(".hc-bb"); });
    addBtns.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        if (state.plan === "subscribe") {
          addSubscribe(btn);
        } else {
          // one-time always prompts the subscribe nudge first
          openModal();
        }
      });
    });

    /* ----- sticky ATC sheet: pick flock + plan (+ duo), then confirm ----- */
    initSheet();
    function initSheet() {
      var sheet = document.querySelector("[data-hc-sheet]");
      if (!sheet) return;
      var sheetState = { bags: state.bags, interval: state.interval, mid: state.mid, range: state.range, plan: state.plan, sellingPlan: state.sellingPlan, qty: state.qty, duo: state.duo };

      function syncFromState() {
        sheetState.bags = state.bags;
        sheetState.interval = state.interval;
        sheetState.mid = state.mid;
        sheetState.range = state.range;
        sheetState.plan = state.plan;
        sheetState.sellingPlan = state.sellingPlan;
        /* subscribe mode: preview the one-time row at the flock-matched bag count,
           not the main box's volume-upsell qty (avoids a surprising "$57" preview) */
        sheetState.qty = state.plan === "onetime" ? state.qty : Math.max(1, state.bags);
        sheetState.duo = state.duo;
      }

      function sheetOneTimeTotal() { return oneTimeTotalFor(sheetState.qty); }

      function sheetPerDayText() {
        var perDayC = (sheetState.bags * subPrice) / (sheetState.interval * 30);
        var perHenC = perDayC / (sheetState.mid || 3);
        var henCeil = Math.ceil(perHenC);
        var henWord = (henCeil > perHenC) ? "under " : "about ";
        return "about " + money(perDayC) + "/day - " + henWord + henCeil + "¢ a hen";
      }

      var sFbtns = Array.prototype.slice.call(sheet.querySelectorAll("[data-hc-sheet-flock]"));
      var sTierBtns = Array.prototype.slice.call(sheet.querySelectorAll("[data-hc-sheet-tier]"));
      var sStepBtns = Array.prototype.slice.call(sheet.querySelectorAll("[data-hc-sheet-step]"));
      var sStepInput = sheet.querySelector("[data-hc-sheet-step-input]");
      var sNudge = sheet.querySelector("[data-hc-sheet-nudge]");
      var sPlanLabels = Array.prototype.slice.call(sheet.querySelectorAll("[data-hc-sheet-planlabel]"));
      var sQtyWrap = sheet.querySelector("[data-hc-sheet-qty-wrap]");
      var sReadoutText = sheet.querySelector("[data-hc-sheet-readout-text]");
      var sPerDay = sheet.querySelector("[data-hc-sheet-perday]");
      var sOtNote = sheet.querySelector("[data-hc-sheet-otnote]");
      var sDuoWrap = sheet.querySelector("[data-hc-sheet-duo-wrap]");
      var sDuoBtns = Array.prototype.slice.call(sheet.querySelectorAll("[data-hc-sheet-duo]"));
      var sCta = sheet.querySelector("[data-hc-sheet-cta]");
      var scrims = Array.prototype.slice.call(sheet.querySelectorAll("[data-hc-sheet-close]"));
      var sAdd = sheet.querySelector("[data-hc-sheet-add]");

      function sheetSyncVisibility() {
        var onetime = sheetState.plan === "onetime";
        if (sQtyWrap) sQtyWrap.hidden = !onetime;
        if (sDuoWrap) sDuoWrap.hidden = onetime;
      }

      function sheetSyncTierCards() {
        sTierBtns.forEach(function (card) {
          var q = parseInt(card.getAttribute("data-qty"), 10) || 1;
          var on = q === sheetState.qty;
          card.classList.toggle("is-active", on);
          card.setAttribute("aria-checked", on ? "true" : "false");
        });
        if (sStepInput && document.activeElement !== sStepInput) sStepInput.value = sheetState.qty;
      }

      function sheetFlockSuggestion() {
        if (sheetState.interval === 1) {
          return sheetState.range + " hens = about " + sheetState.bags + " " + bagWord(sheetState.bags) + "/month.";
        }
        return sheetState.range + " hens = 1 bag lasts about " + sheetState.interval + " months.";
      }

      function sheetRender() {
        var n = sheetState.bags;
        var base = price * n;
        var sub = subPrice * n;
        var duoAdd = sheetState.duo ? eggSubPrice : 0;
        var otTotal = sheetOneTimeTotal();
        sheet.querySelectorAll("[data-hc-sheet-sub-now]").forEach(function (el) { el.textContent = money(sub + duoAdd); });
        sheet.querySelectorAll("[data-hc-sheet-ot-now]").forEach(function (el) { el.textContent = money(otTotal); });
        sheet.querySelectorAll("[data-hc-sheet-subpct]").forEach(function (el) { el.textContent = subPct + "%"; });
        if (sPerDay) sPerDay.textContent = sheetPerDayText();
        if (sOtNote) sOtNote.textContent = sheetState.qty + " " + bagWord(sheetState.qty) + " · +" + money(Math.max(0, oneTimePerBag(sheetState.qty) - subPrice)) + "/bag vs Subscribe · " + shipNote(otTotal);
        fillTierCards(sheet);
        sheetSyncTierCards();
        syncDuoButtons(sDuoBtns, sheetState.duo);
        if (sNudge) sNudge.textContent = oneTimeNudge(sheetState.qty);
        if (sReadoutText) {
          if (sheetState.plan === "onetime") {
            var disc = tierDisc(sheetState.qty);
            var full = price * sheetState.qty;
            var per = Math.round(otTotal / sheetState.qty);
            if (disc > 0) {
              sReadoutText.textContent = sheetFlockSuggestion() + " " + sheetState.qty + " " + bagWord(sheetState.qty) + " for " + money(otTotal) + " (" + money(per) + "/ea) - you save " + money(full - otTotal) + " at checkout.";
            } else {
              sReadoutText.textContent = sheetFlockSuggestion() + " 1 bag, " + money(otTotal) + ". Add a 2nd bag to start saving 5%.";
            }
          } else {
            sReadoutText.textContent = coverageSentence(sheetState.bags, sheetState.interval);
          }
        }
        var active = sheetState.plan === "subscribe" ? sub + duoAdd : otTotal;
        var label = (sheetState.plan === "subscribe" && sheetState.duo) ? "Add Duo to Cart - " : "Add to Cart - ";
        if (sCta) sCta.textContent = label + money(active);
      }

      function applySheetTier(b) {
        sheetState.bags = parseInt(b.getAttribute("data-bags"), 10) || 1;
        sheetState.interval = parseInt(b.getAttribute("data-interval"), 10) || 1;
        sheetState.mid = parseInt(b.getAttribute("data-mid"), 10) || 3;
        sheetState.range = b.getAttribute("data-range") || "";
        sheetState.sellingPlan = parseInt(b.getAttribute("data-plan"), 10) || data.plan1mo;
      }
      function setSheetQty(q) {
        q = parseInt(q, 10);
        if (isNaN(q) || q < 1) q = 1;
        sheetState.qty = q;
        sheetRender();
      }

      sFbtns.forEach(function (b) {
        b.addEventListener("click", function () {
          sFbtns.forEach(function (x) { x.classList.remove("is-active"); });
          b.classList.add("is-active");
          applySheetTier(b);
          if (sheetState.plan === "onetime") sheetState.qty = sheetState.bags;
          sheetRender();
        });
      });
      sTierBtns.forEach(function (card) {
        card.addEventListener("click", function () { setSheetQty(card.getAttribute("data-qty")); });
      });
      sStepBtns.forEach(function (b) {
        b.addEventListener("click", function () {
          var delta = parseInt(b.getAttribute("data-hc-sheet-step"), 10) || 0;
          setSheetQty(sheetState.qty + delta);
        });
      });
      if (sStepInput) {
        sStepInput.addEventListener("input", function () {
          var v = parseInt(sStepInput.value, 10);
          if (isNaN(v) || v < 1) return;
          sheetState.qty = v;
          sheetRender();
        });
        sStepInput.addEventListener("blur", function () { setSheetQty(sStepInput.value); });
      }
      sDuoBtns.forEach(function (b) {
        b.addEventListener("click", function () {
          sheetState.duo = b.getAttribute("data-hc-sheet-duo") === "duo";
          sheetRender();
        });
      });
      sheet.querySelectorAll("[data-hc-sheet-plan]").forEach(function (input) {
        input.addEventListener("change", function () {
          if (!input.checked) return;
          sheetState.plan = input.value;
          /* the slim sheet has no qty UI, so the one-time qty ALWAYS mirrors the
             flock answer - the price shown can never hide a stale bag count */
          if (sheetState.plan === "onetime") sheetState.qty = Math.max(1, sheetState.bags);
          sPlanLabels.forEach(function (l) {
            l.classList.toggle("is-on", l.getAttribute("data-hc-sheet-planlabel") === sheetState.plan);
          });
          sheetSyncVisibility();
          sheetRender();
        });
      });

      function openSheet() {
        syncFromState();
        /* reflect current main state into the sheet controls (match by bags + interval) */
        sFbtns.forEach(function (x) {
          var match = parseInt(x.getAttribute("data-bags"), 10) === sheetState.bags &&
            parseInt(x.getAttribute("data-interval"), 10) === sheetState.interval;
          x.classList.toggle("is-active", match);
        });
        sheet.querySelectorAll("[data-hc-sheet-plan]").forEach(function (input) {
          input.checked = input.value === sheetState.plan;
        });
        sPlanLabels.forEach(function (l) {
          l.classList.toggle("is-on", l.getAttribute("data-hc-sheet-planlabel") === sheetState.plan);
        });
        sheetSyncVisibility();
        sheetRender();
        sheet.setAttribute("aria-hidden", "false");
        sheet.classList.add("is-open");
        document.documentElement.style.overflow = "hidden";
      }
      function closeSheet() {
        sheet.setAttribute("aria-hidden", "true");
        sheet.classList.remove("is-open");
        document.documentElement.style.overflow = "";
      }

      scrims.forEach(function (el) { el.addEventListener("click", closeSheet); });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && sheet.classList.contains("is-open")) closeSheet();
      });

      /* the sticky add button OPENS the sheet (never silently adds 1) */
      var stickyAddBtn = document.querySelector("[data-hc-sticky-add]");
      if (stickyAddBtn) {
        stickyAddBtn.addEventListener("click", function (e) {
          e.preventDefault();
          openSheet();
        });
      }

      if (sAdd) {
        sAdd.addEventListener("click", function (e) {
          e.preventDefault();
          /* push the sheet selection into the main state, then add */
          state.bags = sheetState.bags;
          state.interval = sheetState.interval;
          state.mid = sheetState.mid;
          state.range = sheetState.range;
          state.sellingPlan = sheetState.sellingPlan;
          state.plan = sheetState.plan;
          state.qty = sheetState.qty;
          state.duo = sheetState.duo;
          fbtns.forEach(function (x) {
            var match = parseInt(x.getAttribute("data-bags"), 10) === state.bags &&
              parseInt(x.getAttribute("data-interval"), 10) === state.interval;
            x.classList.toggle("is-active", match);
          });
          var mainRadio = bb.querySelector('[data-hc-plan][value="' + state.plan + '"]');
          if (mainRadio) mainRadio.checked = true;
          planLabels.forEach(function (l) {
            l.classList.toggle("is-on", l.getAttribute("data-hc-planlabel") === state.plan);
          });
          syncSelectorVisibility();
          updateReadout();
          render();
          closeSheet();
          if (state.plan === "subscribe") addSubscribe(sAdd);
          else addOneTime(sAdd);
        });
      }
    }

    if (modal) {
      var modalSub = modal.querySelector("[data-hc-modal-sub]");
      var modalKeep = modal.querySelector("[data-hc-modal-keep]");
      var modalScrim = modal.querySelector("[data-hc-submodal-close]");
      if (modalSub) modalSub.addEventListener("click", function () {
        // switch to subscribe + add with selling plan.
        // Keep the customer's bag count: convert the one-time qty into the
        // subscription (monthly cadence) instead of silently dropping to the
        // flock-pill default.
        if (state.qty > 1 && state.qty !== state.bags) {
          var matchPill = fbtns.filter(function (x) {
            return parseInt(x.getAttribute("data-bags"), 10) === state.qty &&
              parseInt(x.getAttribute("data-interval"), 10) === 1;
          })[0];
          fbtns.forEach(function (x) { x.classList.remove("is-active"); });
          if (matchPill) {
            matchPill.classList.add("is-active");
            applyTier(matchPill);
          } else {
            state.bags = state.qty;
            state.interval = 1;
            state.sellingPlan = data.plan1mo || state.sellingPlan;
          }
        }
        state.plan = "subscribe";
        var subRadio = bb.querySelector('[data-hc-plan][value="subscribe"]');
        if (subRadio) { subRadio.checked = true; }
        planLabels.forEach(function (l) { l.classList.toggle("is-on", l.getAttribute("data-hc-planlabel") === "subscribe"); });
        syncSelectorVisibility();
        updateReadout();
        render();
        closeModal();
        addSubscribe(modalSub);
      });
      if (modalKeep) modalKeep.addEventListener("click", function () {
        closeModal();
        addOneTime(modalKeep);
      });
      if (modalScrim) modalScrim.addEventListener("click", closeModal);
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && modal.classList.contains("is-open")) closeModal();
      });
    }

    /* ----- gallery ----- */
    initGallery(bb);

    /* ----- sticky bar ----- */
    var sticky = document.querySelector("[data-hc-sticky]");
    var inlineAdd = addBtns.filter(function (b) { return !b.hasAttribute("data-hc-sticky-add"); })[0];
    if (sticky && inlineAdd && "IntersectionObserver" in window) {
      var sio = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            sticky.classList.remove("is-on");
            sticky.setAttribute("aria-hidden", "true");
          } else {
            sticky.classList.add("is-on");
            sticky.setAttribute("aria-hidden", "false");
          }
        });
      }, { rootMargin: "0px 0px 0px 0px", threshold: 0 });
      sio.observe(inlineAdd);
    }

    /* ----- first paint ----- */
    syncSelectorVisibility();
    updateReadout();
    render();
  }

  /* ---------- Gallery: scroll-snap track, arrows, dots, thumbs, zoom ---------- */
  function initGallery(bb) {
    var track = bb.querySelector("[data-hc-gtrack]");
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll("[data-hc-gslide], .hc-bb__slide"));
    if (!slides.length) return;

    var dotsWrap = bb.querySelector("[data-hc-gdots]");
    var thumbs = Array.prototype.slice.call(bb.querySelectorAll("[data-hc-gthumb]"));
    var prev = bb.querySelector("[data-hc-gprev]");
    var next = bb.querySelector("[data-hc-gnext]");

    /* build dots */
    var dots = [];
    if (dotsWrap) {
      dotsWrap.innerHTML = "";
      slides.forEach(function (s, i) {
        var d = document.createElement("button");
        d.type = "button";
        d.className = "hc-bb__dot" + (i === 0 ? " is-active" : "");
        d.setAttribute("aria-label", "Go to image " + (i + 1));
        d.addEventListener("click", function () { scrollToIndex(i); });
        dotsWrap.appendChild(d);
        dots.push(d);
      });
    }

    function slideWidth() {
      return slides[0] ? slides[0].getBoundingClientRect().width : track.clientWidth;
    }

    function scrollToIndex(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slideWidth() * i, behavior: "smooth" });
    }

    function currentIndex() {
      var w = slideWidth();
      if (!w) return 0;
      return Math.round(track.scrollLeft / w);
    }

    function setActive(i) {
      dots.forEach(function (d, di) { d.classList.toggle("is-active", di === i); });
      thumbs.forEach(function (t) {
        t.classList.toggle("is-active", parseInt(t.getAttribute("data-hc-gthumb"), 10) === i);
      });
    }

    if (prev) prev.addEventListener("click", function () { scrollToIndex(currentIndex() - 1); });
    if (next) next.addEventListener("click", function () { scrollToIndex(currentIndex() + 1); });

    thumbs.forEach(function (t) {
      t.addEventListener("click", function () {
        var i = parseInt(t.getAttribute("data-hc-gthumb"), 10) || 0;
        scrollToIndex(i);
        setActive(i);
      });
    });

    var raf = null;
    track.addEventListener("scroll", function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = null;
        setActive(currentIndex());
      });
    }, { passive: true });

    /* click any gallery image -> open a full-screen lightbox showing EVERY image
       in a vertically scrollable/swipeable stack, scrolled to the clicked one.
       Click anywhere on the backdrop (anything but an image) closes. */
    var overlay = null, overlayStack = null;
    function buildOverlay() {
      overlay = document.createElement("div");
      overlay.className = "hc-bb__zoom";
      overlay.innerHTML =
        '<button type="button" class="hc-bb__zoom-close" aria-label="Close">&times;</button>' +
        '<div class="hc-bb__zoom-stack" data-hc-zoom-stack></div>';
      document.body.appendChild(overlay);
      overlayStack = overlay.querySelector("[data-hc-zoom-stack]");

      /* populate the stack with every gallery image (full-res) */
      slides.forEach(function (s) {
        var img = s.querySelector("img");
        if (!img) return;
        var big = document.createElement("img");
        big.className = "hc-bb__zoom-img";
        big.src = img.currentSrc || img.src;
        big.alt = img.alt || "";
        big.loading = "lazy";
        overlayStack.appendChild(big);
      });

      /* tap anywhere (incl. an image) closes; swipe-scroll still moves through images
         because a drag does not fire a click. */
      overlay.addEventListener("click", function () { closeZoom(); });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && overlay && overlay.classList.contains("is-open")) closeZoom();
      });
    }
    function openZoom(index) {
      if (!overlay) buildOverlay();
      overlay.classList.add("is-open");
      document.documentElement.style.overflow = "hidden";
      /* scroll the stack to the clicked image first */
      var imgs = overlayStack.querySelectorAll(".hc-bb__zoom-img");
      var target = imgs[index];
      if (target) {
        /* instant jump (no animation) so it opens already on the right image */
        overlayStack.scrollTop = target.offsetTop - overlayStack.offsetTop;
      }
    }
    function closeZoom() {
      if (!overlay) return;
      overlay.classList.remove("is-open");
      document.documentElement.style.overflow = "";
    }
    slides.forEach(function (s, i) {
      var img = s.querySelector("img");
      if (!img) return;
      s.style.cursor = "zoom-in";
      img.addEventListener("click", function () { openZoom(i); });
    });

    setActive(0);
  }

  function boot() { initSubnav(); initIngredients(); initAnchorLinks(); initBuyBox(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
